import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Switch, message, Form, Input } from "antd";
import { Helmet } from "react-helmet-async";
import { PlusOutlined, EyeOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import Header from "../../../components/Header";
import Sidebar from "../../../components/Sidebar";
import Breadcrumb from '../../../components/Common/Breadcrumb';
import LoadingSpinner from "../../../components/Common/LoadingSpinner";
import { onShowSizeChange, itemRender } from "../../../components/Pagination";
import { 
  createMarquee, 
  updateMarquee, 
  fetchAllMarquee, 
  fetchMarqueeById,
  deleteMarquee 
} from "../../../services/marqueeService";
import "./Marquee.css";

const Marquee = () => {
  const [marquees, setMarquees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [selectedMarquee, setSelectedMarquee] = useState(null);
  const [form] = Form.useForm();
  const [submitLoading, setSubmitLoading] = useState(false);

  const breadcrumbItems = [
    { label: "Notification Management" },
    { label: "Marquee" }
  ];

  // Fetch marquees from backend
  useEffect(() => {
    const fetchMarquees = async () => {
      setLoading(true);
      try {
        const data = await fetchAllMarquee();
        // Transform backend data to match frontend structure
        const transformedData = data.map(item => ({
          id: item.marquee_id,
          title: item.marquee_title,
          redirect_url: item.marquee_link,
          status: item.marquee_status
        }));
        setMarquees(transformedData);
      } catch (error) {
        console.error("Error fetching marquees:", error);
        message.error(error.message || "Failed to fetch marquees");
      } finally {
        setLoading(false);
      }
    };
    fetchMarquees();
  }, []);

  // Handle status toggle
  const handleStatusChange = async (marqueeId, currentStatus) => {
    try {
      const newStatus = currentStatus === "active" ? "inactive" : "active";
      
      await updateMarquee(marqueeId, {
        title: marquees.find(m => m.id === marqueeId)?.title,
        link: marquees.find(m => m.id === marqueeId)?.redirect_url,
        status: newStatus
      });

      setMarquees(prevMarquees =>
        prevMarquees.map(marquee =>
          marquee.id === marqueeId
            ? { ...marquee, status: newStatus }
            : marquee
        )
      );
      message.success(`Marquee ${newStatus === "active" ? "activated" : "deactivated"} successfully`);
    } catch (error) {
      console.error(error);
      message.error(error.message || "Failed to update status");
    }
  };

  // Handle view marquee
  const handleViewMarquee = (marquee) => {
    setSelectedMarquee(marquee);
    setIsViewModalVisible(true);
  };

  // Handle edit marquee
  const handleEditMarquee = (marquee) => {
    setSelectedMarquee(marquee);
    form.setFieldsValue({
      title: marquee.title,
      redirect_url: marquee.redirect_url
    });
    setIsEditModalVisible(true);
  };

  // Handle add new marquee
  const handleAddMarquee = () => {
    form.resetFields();
    setIsAddModalVisible(true);
  };

  // Handle form submission for add
  const handleAddSubmit = async (values) => {
    setSubmitLoading(true);
    try {
      await createMarquee({
        title: values.title,
        link: values.redirect_url,
        status: "active"
      });

      // Refresh the marquees list
      const data = await fetchAllMarquee();
      const transformedData = data.map(item => ({
        id: item.marquee_id,
        title: item.marquee_title,
        redirect_url: item.marquee_link,
        status: item.marquee_status
      }));
      setMarquees(transformedData);
      
      message.success("Marquee created successfully");
      setIsAddModalVisible(false);
      form.resetFields();
    } catch (error) {
      message.error(error.message || "Failed to create marquee");
    } finally {
      setSubmitLoading(false);
    }
  };

  // Handle form submission for edit
  const handleEditSubmit = async (values) => {
    setSubmitLoading(true);
    try {
      await updateMarquee(selectedMarquee.id, {
        title: values.title,
        link: values.redirect_url,
        status: selectedMarquee.status
      });

      // Refresh the marquees list
      const data = await fetchAllMarquee();
      const transformedData = data.map(item => ({
        id: item.marquee_id,
        title: item.marquee_title,
        redirect_url: item.marquee_link,
        status: item.marquee_status
      }));
      setMarquees(transformedData);
      
      message.success("Marquee updated successfully");
      setIsEditModalVisible(false);
    } catch (error) {
      message.error(error.message || "Failed to update marquee");
    } finally {
      setSubmitLoading(false);
    }
  };

  // Handle delete marquee
  const handleDeleteMarquee = async (marqueeId) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this marquee?',
      content: 'This action cannot be undone.',
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      async onOk() {
        try {
          await deleteMarquee(marqueeId);
          setMarquees(prevMarquees => prevMarquees.filter(marquee => marquee.id !== marqueeId));
          message.success('Marquee deleted successfully');
        } catch (error) {
          message.error(error.message || 'Failed to delete marquee');
        }
      }
    });
  };

  // Handle redirect from marquee
  const handleRedirect = (url) => {
    if (url) {
      window.open(url, '_blank');
    }
  };

  // Table Columns
  const columns = [
    {
      title: "Sr. No",
      key: "srno",
      render: (text, record, index) => index + 1,
      width: 80,
    },
    {
      title: "Marquee Title",
      dataIndex: "title",
      key: "title",
      render: (title) => (
        <span className="fw-semibold">{title}</span>
      ),
    },
    {
      title: "Redirect URL",
      dataIndex: "redirect_url",
      key: "redirect_url",
      render: (url) => (
        <span className="text-muted">{url || 'No URL'}</span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status, record) => (
        <Switch
          checked={status === "active"}
          onChange={() => handleStatusChange(record.id, status)}
          size="small"
        />
      ),
      width: 100,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <div className="action-buttons">
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleViewMarquee(record)}
            className="btn-view"
          >
            View
          </Button>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditMarquee(record)}
            className="btn-edit"
          >
            Edit
          </Button>
          <Button
            type="link"
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteMarquee(record.id)}
            className="btn-delete"
            danger
          >
            Delete
          </Button>
        </div>
      ),
      width: 200,
    },
  ];

  return (
    <div>
      <Helmet><title>Marquee | CRM Jnec</title></Helmet>
      <Header />
      <Sidebar activeClassName="notification/marquee" />
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <Breadcrumb items={breadcrumbItems} />
              </div>
            </div>
          </div>

          {loading ? (
            <LoadingSpinner message="Loading marquees..." />
          ) : (
            <div className="card card-table show-entire">
              <div className="card-header">
                <h4 className="card-title">Marquee Management</h4>
                <p className="card-text">Manage website scrolling announcements</p>
                <div className="card-actions">
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleAddMarquee}
                    className="btn btn-primary"
                  >
                    Add New Marquee
                  </Button>
                </div>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <Table
                    columns={columns}
                    dataSource={marquees}
                    rowKey="id"
                    pagination={{
                      total: marquees.length,
                      showTotal: (total, range) =>
                        `Showing ${range[0]} to ${range[1]} of ${total} entries`,
                      onShowSizeChange,
                      itemRender,
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Full Page Marquee Preview */}
          {isViewModalVisible && (
            <div className="marquee-preview-overlay">
              {/* Brown background */}
              <div className="marquee-background" />
              
              {/* Close button */}
              <div className="close-button" onClick={() => setIsViewModalVisible(false)}>
                ✕
              </div>
              
              {/* Marquee text */}
              <div className="marquee-container">
                <div 
                  className="marquee-text"
                  onClick={() => selectedMarquee && handleRedirect(selectedMarquee.redirect_url)}
                  style={{ cursor: selectedMarquee?.redirect_url ? 'pointer' : 'default' }}
                >
                  {selectedMarquee?.title}
                </div>
              </div>
              
              {/* Click hint */}
              {selectedMarquee?.redirect_url && (
                <div className="click-hint">
                  Click on the text to visit the link
                </div>
              )}
            </div>
          )}

          {/* Add Marquee Modal */}
          <Modal
            title="Add New Marquee"
            open={isAddModalVisible}
            onCancel={() => !submitLoading && setIsAddModalVisible(false)}
            footer={null}
            width={600}
            maskClosable={!submitLoading}
          >
            <Form
              form={form}
              layout="vertical"
              onFinish={handleAddSubmit}
            >
              <Form.Item
                name="title"
                label="Marquee Title"
                rules={[{ required: true, message: 'Please enter marquee title' }]}
              >
                <Input placeholder="Enter marquee title" />
              </Form.Item>

              <Form.Item
                name="redirect_url"
                label="Redirect URL"
                rules={[{ required: true, message: 'Please enter redirect URL' }]}
              >
                <Input placeholder="Enter redirect URL" />
              </Form.Item>

              <Form.Item className="text-right mb-0">
                <Button 
                  onClick={() => setIsAddModalVisible(false)} 
                  style={{ marginRight: 8 }}
                  disabled={submitLoading}
                >
                  Cancel
                </Button>
                <Button 
                  type="primary" 
                  htmlType="submit"
                  loading={submitLoading}
                >
                  Create Marquee
                </Button>
              </Form.Item>
            </Form>
          </Modal>

          {/* Edit Marquee Modal */}
          <Modal
            title="Edit Marquee"
            open={isEditModalVisible}
            onCancel={() => !submitLoading && setIsEditModalVisible(false)}
            footer={null}
            width={600}
            maskClosable={!submitLoading}
          >
            <Form
              form={form}
              layout="vertical"
              onFinish={handleEditSubmit}
            >
              <Form.Item
                name="title"
                label="Marquee Title"
                rules={[{ required: true, message: 'Please enter marquee title' }]}
              >
                <Input placeholder="Enter marquee title" />
              </Form.Item>

              <Form.Item
                name="redirect_url"
                label="Redirect URL"
                rules={[{ required: true, message: 'Please enter redirect URL' }]}
              >
                <Input placeholder="Enter redirect URL" />
              </Form.Item>

              <Form.Item className="text-right mb-0">
                <Button 
                  onClick={() => setIsEditModalVisible(false)} 
                  style={{ marginRight: 8 }}
                  disabled={submitLoading}
                >
                  Cancel
                </Button>
                <Button 
                  type="primary" 
                  htmlType="submit"
                  loading={submitLoading}
                >
                  Update Marquee
                </Button>
              </Form.Item>
            </Form>
          </Modal>
        </div>
      </div>
    </div>
  );
}

export default Marquee;